import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import ClosingCta from "@/components/sections/ClosingCta";
import useBlogArticles from "@/hooks/useBlogArticles";

const formatDate = (value) =>
  new Date(value).toLocaleDateString("en-SG", { year: "numeric", month: "long", day: "numeric" });

function ArticleRow({ article }) {
  return (
    <Link
      to={`/insights/${article.slug}?id=${article.id}`}
      className="card-lift block bg-[#EADCC6] rounded-2xl border border-[#1F1F1C]/10 p-7"
      data-testid={`article-card-${article.slug}`}
    >
      <p className="text-xs uppercase tracking-[0.16em] font-semibold text-[#4A463F]">
        {formatDate(article.created_at)}
      </p>
      <h2 className="mt-3 font-fraunces font-extrabold text-base md:text-lg text-[#1F1F1C] leading-snug">
        {article.title}
      </h2>
      {(article.meta_description || article.excerpt) && (
        <p className="mt-3 text-sm text-[#4A463F] leading-relaxed line-clamp-3">
          {article.meta_description || article.excerpt}
        </p>
      )}
      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C8703A]">
        Read it <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
      </span>
    </Link>
  );
}

export default function InsightsPage() {
  const { articles, loading, error, retry } = useBlogArticles();

  return (
    <main data-testid="insights-page">
      <Seo
        title="Insights"
        description="Notes on sales automation, AI workflows and outbound from Raki Solutions in Singapore."
        path="/insights"
      />
      <PageHero
        label="Insights"
        headline="Notes on selling and automating it."
        subline="What we learn building sales workflows for teams in Singapore and across Asia."
      />

      <section className="px-6 sm:px-8 pb-20 sm:pb-28">
        <div className="max-w-4xl mx-auto">
          {loading && (
            <div className="grid sm:grid-cols-2 gap-5" data-testid="insights-loading">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="bg-[#EADCC6] rounded-2xl border border-[#1F1F1C]/10 p-7 animate-pulse">
                  <div className="h-3 w-28 rounded bg-[#1F1F1C]/10" />
                  <div className="mt-4 h-5 w-4/5 rounded bg-[#1F1F1C]/15" />
                  <div className="mt-4 h-3 w-full rounded bg-[#1F1F1C]/10" />
                  <div className="mt-2 h-3 w-2/3 rounded bg-[#1F1F1C]/10" />
                </div>
              ))}
            </div>
          )}
          {!loading && error && (
            <div className="bg-[#EADCC6] rounded-2xl border border-[#1F1F1C]/10 p-8 text-center" data-testid="insights-error">
              <p className="text-sm text-[#4A463F]">Articles are taking longer than usual to load.</p>
              <button
                onClick={retry}
                className="btn-press mt-6 inline-flex items-center justify-center rounded-2xl bg-[#C8703A] text-[#F4E9D8] px-6 py-3 text-sm font-semibold hover:bg-[#B35F2C]"
                data-testid="insights-retry"
              >
                Try again
              </button>
            </div>
          )}
          {!loading && !error && articles.length === 0 && (
            <div className="bg-[#EADCC6] rounded-2xl border border-dashed border-[#1F1F1C]/20 px-8 py-16 text-center" data-testid="insights-empty">
              <p className="text-base text-[#4A463F]">Articles coming soon</p>
            </div>
          )}
          {!loading && !error && articles.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-5" data-testid="insights-grid">
              {articles.map((a) => (
                <ArticleRow key={a.id} article={a} />
              ))}
            </div>
          )}
        </div>
      </section>

      <ClosingCta testId="insights-closing-cta" />
    </main>
  );
}
